import { CircularProgress, Typography, Button, Alert } from '@mui/material';
import { Grid } from '@mui/material';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { Box, Container } from '@mui/system';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import StandardPage from '~/layouts/StandardPage';
import { ERROR_MESSAGE, STATUS } from '~/literals';
import { RootState, PaymentRequest } from '~/types';
import { useParams, useNavigate } from 'react-router-dom';
import {
  setBookingStepToPassengerDetailsCapturing
} from '~/actions';

const FlightPayment = () => {
  const { register } = useForm<Record<string, any>>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const paymentRequest = useSelector<RootState, PaymentRequest | null>(({ flightBooking }) => flightBooking.paymentRequest);
  const status = useSelector<RootState, string>(({ flightBooking }) => flightBooking.pageStatus);
  const paymentError = useSelector<RootState, string | undefined>(({ flightBooking }) => flightBooking.errorMessage);
  const submitButtonRef = useRef<HTMLButtonElement>(null);


  const triggerSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const backToDetailsCapture = () => {
    dispatch(setBookingStepToPassengerDetailsCapturing());
    navigate(`/flights/pre-booking/${id}`);
  }

  const backToHome = () => {
    dispatch(setBookingStepToPassengerDetailsCapturing());
    navigate(`/`);
  }

  useEffect(() => {
    if (paymentRequest && !paymentError) {
      triggerSubmit();
    }
  }, [paymentRequest]);

  return (
    <StandardPage
      bannerContent={
        <Typography variant="h4" fontWeight={600} mb={2} color="white">
          Payment
        </Typography>
      }
    >
      {
        status == STATUS.RUNNING &&
        <Container sx={{ textAlign: 'center' }}>
          <Typography variant='body1'>Please wait</Typography>
          <CircularProgress />
        </Container>
      }
      <Box>
        {
          (status == STATUS.SUCCESS && paymentRequest && !paymentError && paymentRequest.paymentGatewayPostData) &&
          <Box component='form' action={paymentRequest.paymentGatewayRedirectUrl} method={paymentRequest.method || 'post'}>
            {
              Object.keys(paymentRequest.paymentGatewayPostData).map(key => (
                <input
                  key={key}
                  type="hidden"
                  {...register(key)}
                  value={paymentRequest.paymentGatewayPostData[key]}
                />
              ))
            }
            <button type='submit' ref={submitButtonRef} style={{ display: 'none' }}>Submit</button>
          </Box>
        }
        {
          ((paymentError || status == STATUS.ERROR) && status != STATUS.RUNNING && paymentError != ERROR_MESSAGE.PRICE_CHANGED) &&
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3} textAlign="center">
              <SentimentDissatisfiedIcon color="error" fontSize="large" style={{ fontSize: 100 }} />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <Alert severity="error">An error occurred while trying to request for payment.</Alert>
              <Typography variant='body1' textAlign="center" mt={10}>
                <Button
                  onClick={backToDetailsCapture}
                  variant="contained"
                  size="small"
                >
                  Try again
                </Button>
              </Typography>
            </Grid>
          </Grid>
        }
        {
          (status == STATUS.ERROR && paymentError == ERROR_MESSAGE.PRICE_CHANGED) &&
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3} textAlign="center">
              <ProductionQuantityLimitsIcon color="error" style={{ fontSize: 100 }} />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <Alert severity="error">The price of the flight has changed. You can click the button below to search again.</Alert>
              <Typography variant='body1' textAlign="center" mt={10}>
                <Button
                  onClick={backToHome}
                  variant="contained"
                  size="small"
                >
                  Search again
                </Button>
              </Typography>
            </Grid>
          </Grid>
        }
      </Box>
    </StandardPage>
  );
};

export default FlightPayment;
